import { lazy, Suspense, type ComponentProps, type JSX } from 'react';
// Type-only: erased at compile time, so this does NOT pull the library back
// into the main bundle and undo the split below.
import type Cropper from 'react-easy-crop';

/**
 * `react-easy-crop`, split out of the main bundle.
 *
 * Three screens import it — profile photo during sign-up, profile photo in
 * Settings, and a memory photo — but all three only ever render it while the
 * user is actively cropping, which most sessions never do. Imported eagerly it
 * rode along in the initial download for everyone.
 *
 * The fallback is a plain tinted box rather than a spinner: the chunk is small
 * and served from the same origin, so on anything but a cold, slow connection
 * it resolves in a frame or two, and a spinner that flashes for 30ms reads as
 * jank.
 */
const CropperImpl = lazy(() => import('react-easy-crop'));

/**
 * The props JSX itself computes for `<Cropper>`. The library declares its
 * optional props through `defaultProps`, which JSX honours but `ComponentProps`
 * does not — without this every caller would have to pass `rotation`,
 * `minZoom`, `zoomSpeed` and six others explicitly.
 */
type CropperJsxProps = JSX.LibraryManagedAttributes<typeof Cropper, ComponentProps<typeof Cropper>>;

export function LazyCropper(props: CropperJsxProps) {
  return (
    <Suspense fallback={<div className="absolute inset-0 bg-app-faint/60 rounded-2xl" aria-busy="true" />}>
      {/* The cast re-states what JSX already guarantees: React fills the
          defaulted props at runtime, exactly as it would for a direct
          <Cropper>. `lazy()`'s own type just doesn't carry that through. */}
      <CropperImpl {...(props as ComponentProps<typeof Cropper>)} />
    </Suspense>
  );
}
